import { useEffect } from "react";

// Full-screen dialogs in the presentation editor cover the Reveal deck
// completely, but the deck stays mounted underneath and keeps paying for
// itself: it is ~2,800 of the editor's ~4,600 elements, matched against the
// ~7,800 CSS rules in the app bundle. Measured on the production build with a
// full-screen dialog open, one full style recalc costs ~28ms with the deck in
// the tree and ~3ms with it out -- the deck alone is ~89% of it.
//
// That is what froze the window on resize: every tick of a drag invalidates the
// deck (Reveal re-runs layout() and writes inline sizing onto .slides), so those
// ~28ms land once a frame and a two-second drag blocks for seconds. Suspending
// it costs nothing visually, because none of it is on screen.
//
// Only use this for dialogs that are UNCONDITIONALLY full-screen. A dialog that
// is only full-screen below a breakpoint (`fullScreen={useMediaQuery(...)}`)
// renders over a semi-transparent backdrop on larger screens, where the deck
// shows through -- suspending it there would visibly blank the background.
//
// The rule itself is `body.deck-suspended` in
// styles/screens/presentations/_editPresentation.scss, scoped to
// `.edit-presentation .reveal`. That scoping is what makes this safe to call
// from shared components like the media library, which also open on screens
// that have no deck at all: there the class is simply inert.

const BODY_CLASS = "deck-suspended";

// These dialogs nest -- the slide manager opens the add-slide picker, which
// opens the library, which can open the media library. Each suspends
// independently, so the class is reference counted: without this, closing the
// inner dialog would un-suspend the deck while an outer one still covers it.
let suspendCount = 0;

/**
 * Takes the Reveal deck out of the style, layout and paint path while `active`
 * is true. Safe to call from several dialogs at once, and inert on screens that
 * have no deck.
 *
 * @param {boolean} active - usually the dialog's own `open` prop.
 */
export default function useDeckSuspended(active) {
  useEffect(() => {
    if (!active) return undefined;

    suspendCount += 1;
    document.body.classList.add(BODY_CLASS);

    return () => {
      suspendCount -= 1;
      // Only the last dialog to close lifts it. Clamped at zero so a consumer
      // that unmounts without ever having incremented can't strand the class on
      // the body (which would leave the deck invisible).
      if (suspendCount <= 0) {
        suspendCount = 0;
        document.body.classList.remove(BODY_CLASS);
      }
    };
  }, [active]);
}
